import { CORE_CATEGORY_ROOTS, LEGACY_CATEGORY_GROUPINGS, SHOP_ROOT } from '~/utils/categoryRoutes'

// Drop a pre-restructure grouping segment so a legacy path reads as its current
// equivalent: /shop-products/other-products/die-cutting/dies-all becomes
// /shop-products/die-cutting/dies-all. Anything else passes through untouched.
const withoutLegacyGrouping = (slug: string): string => {
  const legacy = LEGACY_CATEGORY_GROUPINGS.find(g => slug.startsWith(`${SHOP_ROOT}/${g}/`))
  return legacy ? `${SHOP_ROOT}/${slug.slice(`${SHOP_ROOT}/${legacy}/`.length)}` : slug
}

// True when `slug` is the core category root itself or anything beneath it.
// The trailing-slash test keeps a sibling that merely shares the prefix (e.g.
// /shop-products/stamps-sale) from counting as Stamps.
const isUnderRoot = (slug: string, root: string): boolean =>
  slug === root || slug.startsWith(`${root}/`)

// Pick the canonical category for a product. Drives breadcrumb display, SEO,
// and GTM `item_category` analytics so all three surfaces agree.
//
// Rule (AB#63832; ported from nuxt2 utils/canonicalCategory.ts, AB#63978):
//   1. Only the core categories in CORE_CATEGORY_ROOTS are ever a breadcrumb.
//      Ways to Craft, campaign branches and other groupings are ignored, so a
//      product still tagged with a not-yet-open campaign (Gift Guide,
//      Flash Sale, Black Friday) can't surface it.
//   2. A product in more than one core category resolves to the earliest one
//      in CORE_CATEGORY_ROOTS' business priority order.
//   3. Within that core category the breadcrumb is the DEEPEST path the
//      product is filed under - deepest meaning the most path segments, not
//      the longest string - so the crumb is as specific as the data allows.
//      Ties are broken by taking the first of the equally deep paths; the
//      category list arrives alphabetically sorted, so that is deterministic.
//      Note this means a catch-all bucket wins its tie — a stamp set filed
//      under stamps-all, stamps-occasion and stamps-season resolves to
//      stamps-all (agreed 2026-09-22).
//      A path under a legacy grouping (LEGACY_CATEGORY_GROUPINGS) counts
//      toward the core category beneath it.
//   4. A product in no core category resolves to the shop root, which renders
//      as Shop Home alone.
//   5. Return '' when there are no category slugs at all.
//
// The shelved Online Exclusives override (a product in Online Exclusives keeps
// that trail whatever else it is filed under) would hook in ahead of step 3.
export function getCanonicalCategory (categorySlugs: readonly string[] | undefined): string {
  // Defensively drop non-string entries: a freshly added wishlist item can arrive with a
  // non-string `category` (e.g. a numeric category id), which would throw on `.startsWith`
  // and surface "s.startsWith is not a function" in GTM/breadcrumb code (AB#60767).
  const slugs = (categorySlugs ?? [])
    .filter((s): s is string => typeof s === 'string')
    .map(withoutLegacyGrouping)
  if (slugs.length === 0) { return '' }

  const coreRoot = CORE_CATEGORY_ROOTS.find(root => slugs.some(s => isUnderRoot(s, root)))
  if (!coreRoot) { return SHOP_ROOT }

  // Deepest path the product carries under that core category, measured in
  // path segments rather than string length. The strict `>` is the tie rule:
  // an equally deep path never displaces the one already held, so the first of
  // the equally deep paths wins. Seeded with the root so a product filed only
  // at the category root still resolves there.
  const depth = (slug: string): number => slug.split('/').filter(Boolean).length
  return slugs
    .filter(s => isUnderRoot(s, coreRoot))
    .reduce((deepest, s) => depth(s) > depth(deepest) ? s : deepest, coreRoot)
}
